import { z } from 'zod';
/**
 * The typed chart contract for AI chat surfaces (plan 009 §2): a chat agent
 * never authors chart code or HTML — it parameterizes a query, the backend
 * recomputes the numbers, and a trusted React component renders THIS shape.
 *
 * Two deliberate omissions:
 *  - **No colors.** The frontend assigns them from `getCategoryColors`, so the
 *    design system keeps ownership and a model cannot pick an unreadable or
 *    inaccessible palette.
 *  - **No regex anywhere.** Tool input schemas derived from these types are sent
 *    to the model provider as JSON Schema, and gpt-5.6 rejects a whole request
 *    when a pattern contains lookaround (the `z.email()` gotcha). Bounds are
 *    expressed as lengths and enums only.
 *
 * Every string is bounded and every numeric is finite (zod v4 rejects `NaN` and
 * `±Infinity` for `z.number()`), so a persisted chart part has a predictable
 * serialized size — see `MAX_CHAT_MESSAGE_PART_SERIALIZED_LENGTH`.
 */ export const CHART_TYPES = [
    'bar',
    'line',
    'pie',
    'area'
];
export const ChartTypeSchema = z.enum(CHART_TYPES);
/** How the frontend formats values and axis ticks. */ export const CHART_UNITS = [
    'hours',
    'currency',
    'count',
    'percent'
];
export const ChartUnitSchema = z.enum(CHART_UNITS);
export const MAX_CHART_SERIES = 4;
export const MAX_CHART_POINTS_PER_SERIES = 24;
export const MAX_CHART_TITLE_LENGTH = 120;
export const MAX_CHART_SERIES_NAME_LENGTH = 60;
export const MAX_CHART_POINT_LABEL_LENGTH = 80;
/**
 * Placeholder point labels the backend cannot name: the group key is an entity
 * the caller may not read, points at nothing, or is unset. The backend fills
 * `label` with an English placeholder for the model's benefit (it reads the
 * projection, not the chart), and the renderer shows `t()` of this key instead —
 * a rendered chart is UI, and UI text is never a raw backend string.
 */ export const CHART_POINT_LABEL_KEYS = [
    'restricted',
    'unknown',
    'unassigned'
];
export const ChartPointLabelKeySchema = z.enum(CHART_POINT_LABEL_KEYS);
export const ChartDataPointSchema = z.object({
    label: z.string().min(1).max(MAX_CHART_POINT_LABEL_LENGTH),
    value: z.number(),
    labelKey: ChartPointLabelKeySchema.optional()
});
export const ChartSeriesSchema = z.object({
    name: z.string().min(1).max(MAX_CHART_SERIES_NAME_LENGTH),
    data: z.array(ChartDataPointSchema).min(1, 'A chart series needs at least one data point').max(MAX_CHART_POINTS_PER_SERIES, `A chart series holds at most ${MAX_CHART_POINTS_PER_SERIES} data points`)
});
export const ChartSpecSchema = z.object({
    type: ChartTypeSchema,
    title: z.string().min(1).max(MAX_CHART_TITLE_LENGTH),
    series: z.array(ChartSeriesSchema).min(1, 'A chart needs at least one series').max(MAX_CHART_SERIES, `A chart holds at most ${MAX_CHART_SERIES} series`),
    unit: ChartUnitSchema.optional()
});
/**
 * The closed v1 metric matrix (plan 009 §6.8) — exactly what the authorized
 * `ctx.data` capacity and entity-count reads support. "Capacity by department" is deliberately
 * absent: no department-capacity pipeline exists, and faking one out of process
 * capacities would double-count shared steps.
 */ export const CHART_METRICS = [
    'capacity',
    'entityCount'
];
export const ChartMetricSchema = z.enum(CHART_METRICS);
/** The single number each point carries — it also fixes the chart's unit. */ export const CHART_MEASURES = [
    'hoursPerYear',
    'hoursPerMonth',
    'costPerYear',
    'costPerMonth',
    'processCount'
];
export const ChartMeasureSchema = z.enum(CHART_MEASURES);
/**
 * What the query saw versus what the chart shows. Required, not decorative: a
 * chart built from a need-to-know principal's scope silently omits everything
 * they may not read, and both the model and the reader must be able to say so
 * instead of presenting a partial picture as complete.
 */ export const ChartCoverageSchema = z.object({
    /** Entities or groups the authorized query considered. */ requested: z.number().int().min(0),
    /** Data points actually plotted. */ rendered: z.number().int().min(0),
    /** Considered but not plotted — unauthorized, missing survey data, or beyond the cap. */ omitted: z.number().int().min(0),
    /** More groups exist than the caps allow: never sum the points and call it a total. */ truncated: z.boolean()
});
/**
 * Where the numbers came from. `source` is a literal because v1 has exactly one
 * arm: the model may never supply org-derived values, and a "user provided"
 * arm could not be enforced against a model-authored tool input (plan 009 §6.8).
 */ export const ChartProvenanceSchema = z.object({
    source: z.literal('tenantMetric'),
    metric: ChartMetricSchema,
    measure: ChartMeasureSchema
});
/**
 * The `renderChart` tool result, and therefore the `output` of a persisted
 * `tool-renderChart` part. The tool validates against this before returning, so
 * everything downstream — persistence, the model text projection, the renderer —
 * can rely on the shape without re-deriving it.
 */ export const RenderChartOutputSchema = z.object({
    spec: ChartSpecSchema,
    coverage: ChartCoverageSchema,
    provenance: ChartProvenanceSchema
});

//# sourceMappingURL=chart-spec.js.map